import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  SwipeableDrawer,
  Box,
  Checkbox,
  Button,
  Typography,
  CircularProgress
} from '@mui/material';
import { useTheme, useMediaQuery } from '@mui/material';
import { bannerApi } from '../services/adminApi';
import { Banner } from '../types/admin';
import { getSafeApiUrl, removeApiFromBaseUrl } from '../config/environment';
import { getStoreId, getStoreName } from '../utils/storeHelper';

const API_BASE_URL = getSafeApiUrl('GATEWAY');

interface BannerDrawerProps {
  onClose?: () => void;
}

const BannerDrawer: React.FC<BannerDrawerProps> = ({ onClose }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [banner, setBanner] = useState<Banner | null>(null);
  
  // 디버깅: 모바일 모드 확인
  useEffect(() => {
    console.log('[BannerDrawer] isMobile:', isMobile, 'anchor:', isMobile ? 'bottom' : 'top');
  }, [isMobile]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [dontShowFor7Days, setDontShowFor7Days] = useState(false);
  const [imageLoading, setImageLoading] = useState(true);
  const [imageError, setImageError] = useState(false);

  useEffect(() => {
    // 알림 창들이 먼저 표시되도록 확인
    const checkAlertsAndLoadBanner = async () => {
      // 알림 다이얼로그가 DOM에 있는지 확인하는 함수
      const checkForAlerts = () => {
        // MUI Dialog나 UnifiedModal이 표시되는지 확인
        const dialogs = document.querySelectorAll('[role="dialog"]');
        const hasActiveDialog = Array.from(dialogs).some(dialog => {
          const style = window.getComputedStyle(dialog);
          return style.display !== 'none' && style.visibility !== 'hidden';
        });
        return hasActiveDialog;
      };

      // 알림 창들이 표시될 시간을 대기 (최대 2초)
      // 알림이 있으면 표시될 시간을 주고, 없으면 바로 배너 표시
      let attempts = 0;
      const maxWaitAttempts = 4; // 2초 (500ms * 4)
      const checkInterval = setInterval(() => {
        attempts++;
        const hasAlerts = checkForAlerts();
        
        if (hasAlerts) {
          // 알림이 있으면 닫힐 때까지 대기
          clearInterval(checkInterval);
          const waitForAlertsToClose = setInterval(() => {
            if (!checkForAlerts()) {
              clearInterval(waitForAlertsToClose);
              loadBanner();
            }
          }, 500);
          
          // 최대 30초 대기 (알림이 너무 오래 열려있으면 배너 표시)
          setTimeout(() => {
            clearInterval(waitForAlertsToClose);
            loadBanner();
          }, 30000);
        } else if (attempts >= maxWaitAttempts) {
          // 2초 동안 알림이 없으면 바로 배너 표시
          clearInterval(checkInterval);
          loadBanner();
        }
      }, 500);
    };

    checkAlertsAndLoadBanner();
  }, []);

  const loadBanner = async () => {
    try {
      setLoading(true);
      const activeBanner = await bannerApi.getActiveBanner();
      
      if (!activeBanner) {
        setOpen(false);
        return;
      }

      // 7일간 보지 않기 체크
      const hideKey = `banner_hide_${activeBanner.id}`;
      const hideData = localStorage.getItem(hideKey);
      if (hideData) {
        const { timestamp } = JSON.parse(hideData);
        const hideDate = new Date(timestamp);
        const now = new Date();
        const daysDiff = (now.getTime() - hideDate.getTime()) / (1000 * 60 * 60 * 24);
        
        if (daysDiff < 7) {
          // 7일이 지나지 않았으면 표시하지 않음
          setOpen(false);
          return;
        } else {
          // 7일이 지났으면 localStorage에서 제거
          localStorage.removeItem(hideKey);
        }
      }

      // 게시 기간 체크
      const now = new Date();
      const startDate = new Date(activeBanner.startDate);
      const endDate = new Date(activeBanner.endDate);

      if (now < startDate || now > endDate) {
        setOpen(false);
        return;
      }

      setBanner(activeBanner);
      setOpen(true);
    } catch (error) {
      console.error('배너 로드 실패:', error);
      // 에러 발생해도 앱 진입은 정상 진행
      setOpen(false);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (dontShowFor7Days && banner) {
      // 7일간 보지 않기 저장
      const hideKey = `banner_hide_${banner.id}`;
      localStorage.setItem(hideKey, JSON.stringify({
        timestamp: new Date().toISOString(),
        bannerId: banner.id
      }));
    }

    setOpen(false);
    if (onClose) {
      onClose();
    }
  };

  const handleImageClick = async () => {
    if (banner?.linkUrl) {
      // 클릭 통계 수집 (비동기, 실패해도 무시)
      if (banner.id) {
        // Store 정보 가져오기
        const storeId = getStoreId();
        let storeName: string | null = null;
        
        if (storeId > 0) {
          try {
            storeName = await getStoreName();
          } catch (error) {
            // StoreName 조회 실패는 무시 (StoreId만 있어도 저장 가능)
            console.warn('⚠️ [BannerDrawer] StoreName 조회 실패:', error);
          }
        }

        // 클릭 로그 저장
        const clickRequest = storeId > 0 ? {
          storeId,
          storeName: storeName || undefined,
          userAgent: navigator.userAgent,
          ipAddress: undefined // 프론트엔드에서는 IP 주소를 알 수 없음
        } : undefined;

        bannerApi.recordClick(banner.id, clickRequest).catch(() => {
          // 통계 수집 실패는 무시
        });
      }

      // 새 창에서 링크 열기
      window.open(banner.linkUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const getImageUrl = () => {
    if (!banner) return '';
    
    const imageUrl = isMobile ? banner.imageUrlMobile : banner.imageUrlDesktop;
    if (!imageUrl) return '';

    // 절대 URL이면 그대로 사용
    if (imageUrl.startsWith('http')) {
      return imageUrl;
    }
    
    // 상대 URL이면 API_BASE_URL 추가
    // 이미지 URL이 /api로 시작하면 API_BASE_URL에서 /api를 제거하여 중복 방지
    if (imageUrl.startsWith('/api')) {
      return `${removeApiFromBaseUrl(API_BASE_URL)}${imageUrl}`;
    }
    
    return `${API_BASE_URL}${imageUrl}`;
  };

  if (loading || !banner || !open) {
    return null;
  }

  const imageUrl = getImageUrl();
  if (!imageUrl) {
    return null;
  }

  const drawerAnchor = isMobile ? 'bottom' : 'top';
  
  return createPortal(
    <SwipeableDrawer
      anchor={drawerAnchor}
      open={open}
      onClose={handleClose}
      onOpen={() => {}} // SwipeableDrawer에서 필수
      keepMounted={false}
      transitionDuration={{
        enter: 0, // 애니메이션 비활성화
        exit: 0  // 애니메이션 비활성화
      }}
      disableDiscovery
      disableBackdropTransition
      disableScrollLock={false}
      hysteresis={0.65}
      minFlingVelocity={400}
      swipeAreaWidth={40}
      ModalProps={{
        keepMounted: true,
        hideBackdrop: false,
        disableEnforceFocus: true, // 포커스 강제 관리 비활성화 (모바일 환경 최적화)
        disableAutoFocus: true,   // 자동 포커스 비활성화 (모바일 환경 최적화)
        disableRestoreFocus: true, // 포커스 복원 비활성화 (모바일 환경 최적화)
        sx: {
          zIndex: 10001, // UnifiedModal(1300-1400), Snackbar(9999)보다 높게 설정
          '& .MuiBackdrop-root': {
            zIndex: 10000, // Backdrop도 높게 설정
            backgroundColor: 'rgba(0, 0, 0, 0.5)'
          }
        },
        style: {
          zIndex: 10001, // 인라인 스타일로도 설정 (더 확실하게)
        }
      }}
      PaperProps={{
        sx: {
          zIndex: 10001, // 모든 알림 창(최대 9999)보다 높게 설정
          willChange: 'transform', // 애니메이션 성능 최적화
          position: 'fixed', // UnifiedModal과 동일하게 fixed 설정
          overflow: 'hidden', // 내부 콘텐츠에서 스크롤 처리
          overscrollBehavior: 'none',
          ...(isMobile
            ? {
                height: 'auto',
                maxHeight: '90vh',
                borderTopLeftRadius: 16,
                borderTopRightRadius: 16,
                display: 'flex',
                flexDirection: 'column'
              }
            : {
                maxHeight: '400px',
                height: '400px', // 고정 높이로 설정
                borderBottomLeftRadius: 16,
                borderBottomRightRadius: 16,
                display: 'flex',
                flexDirection: 'column'
              })
        },
        style: {
          zIndex: 10001, // 인라인 스타일로도 설정 (더 확실하게)
        }
      }}
    >
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          ...(isMobile
            ? {
                display: 'flex',
                flexDirection: 'column',
                minHeight: '100%'
              }
            : {
                display: 'flex',
                flexDirection: 'column',
                height: '100%', // 부모의 400px 높이에 맞춤
                overflow: 'hidden'
              })
        }}
      >
        {/* 이미지 */}
        <Box
          onClick={handleImageClick}
          sx={{
            position: 'relative',
            cursor: banner.linkUrl ? 'pointer' : 'default',
            ...(isMobile
              ? {
                  flex: '0 0 auto',
                  aspectRatio: '1/1',
                  width: '100%',
                  '& img': {
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    display: 'block'
                  }
                }
              : {
                  flex: '1 1 0%', // 남은 공간을 모두 차지 (0%는 최소 높이 없음)
                  width: '100%',
                  minHeight: 0, // flexbox에서 overflow를 위해 필요
                  maxHeight: 'calc(400px - 73px)', // 전체 높이(400px) - 하단 영역 높이(약 73px)
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  overflow: 'hidden',
                  '& img': {
                    maxWidth: '100%',
                    maxHeight: '100%',
                    width: 'auto',
                    height: 'auto',
                    objectFit: 'contain', // 이미지가 잘리지 않고 전체가 보이도록
                    display: 'block'
                  }
                })
          }}
        >
          {imageLoading && (
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)'
              }}
            >
              <CircularProgress />
            </Box>
          )}
          {imageError ? (
            <Box
              sx={{
                width: '100%',
                height: isMobile ? '100vw' : '200px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#f5f5f5'
              }}
            >
              <Typography variant="body2" color="text.secondary">
                이미지를 불러올 수 없습니다.
              </Typography>
            </Box>
          ) : (
            <img
              src={imageUrl}
              alt="배너"
              onLoad={() => setImageLoading(false)}
              onError={() => {
                setImageLoading(false);
                setImageError(true);
              }}
              style={{
                display: imageLoading ? 'none' : 'block',
                width: '100%',
                height: 'auto'
              }}
            />
          )}
        </Box>

        {/* 모바일 하단 고정 바 */}
        {isMobile && (
          <Box
            sx={{
              position: 'sticky',
              bottom: 0,
              width: '100%',
              height: '80px',
              backgroundColor: 'white',
              borderTop: '1px solid #e0e0e0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '0 16px',
              marginTop: 'auto'
            }}
          >
            <Checkbox
              checked={dontShowFor7Days}
              onChange={(e) => setDontShowFor7Days(e.target.checked)}
              sx={{
                '& .MuiSvgIcon-root': {
                  fontSize: 20
                }
              }}
            />
            <Typography variant="body2" sx={{ flex: 1, marginLeft: 1 }}>
              7일간 보지 않기
            </Typography>
            <Button
              variant="contained"
              onClick={handleClose}
              sx={{
                minWidth: 80
              }}
            >
              닫기
            </Button>
          </Box>
        )}

        {/* PC 하단 닫기 영역 */}
        {!isMobile && (
          <Box
            sx={{
              flex: '0 0 auto', // 고정 높이
              padding: 2,
              display: 'flex',
              justifyContent: 'flex-end',
              borderTop: '1px solid #e0e0e0',
              backgroundColor: 'white'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Checkbox
                checked={dontShowFor7Days}
                onChange={(e) => setDontShowFor7Days(e.target.checked)}
              />
              <Typography variant="body2">7일간 보지 않기</Typography>
              <Button variant="outlined" onClick={handleClose}>
                닫기
              </Button>
            </Box>
          </Box>
        )}
      </Box>
    </SwipeableDrawer>,
    document.body
  );
};

export default BannerDrawer;
